// store/slices/stockSlice.ts
import {
  inventoryApi,
  StockBalance,
  StockTransaction,
} from "@/services/inventoryApi";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface StockState {
  entries: StockTransaction[];
  pagination: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  selectedEntry: StockTransaction | null;
  stockBalance: StockBalance | null;
  loading: boolean;
  error: string | null;
  updating: boolean;
}

const initialState: StockState = {
  entries: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  },
  selectedEntry: null,
  stockBalance: null,
  loading: false,
  error: null,
  updating: false,
};

// Thunks
export const fetchStockEntries = createAsyncThunk(
  "stock/fetchAll",
  async ({
    page,
    limit,
    startDate,
    endDate,
    productId,
  }: {
    page: number;
    limit: number;
    startDate?: string;
    endDate?: string;
    productId?: number;
  }) => {
    const response = await inventoryApi.getAll({
      page,
      limit,
      startDate,
      endDate,
      productId,
    });
    return response;
  },
);

export const fetchStockEntryById = createAsyncThunk(
  "stock/fetchById",
  async (id: number) => {
    // Since there is no dedicated GET /inventory/:id, we fetch all and filter.
    // To optimise, you could add a backend endpoint, but here we simulate.
    const response = await inventoryApi.getAll({ page: 1, limit: 1000 });
    const entry = response.transactions.find((t) => t.id === id);
    if (!entry) throw new Error("Transaction not found");
    return entry;
  },
);

export const fetchStockBalance = createAsyncThunk(
  "stock/fetchBalance",
  async (productId: number) => {
    const balance = await inventoryApi.getStockByProduct(productId);
    return balance;
  },
);

export const createStockEntry = createAsyncThunk(
  "stock/create",
  async (data: {
    productId: number;
    type: "in" | "out";
    quantity: number;
    reason: string;
  }) => {
    const { transaction } = await inventoryApi.create(data);
    return transaction;
  },
);

export const deleteStockEntry = createAsyncThunk(
  "stock/delete",
  async (id: number) => {
    await inventoryApi.delete(id);
    return id;
  },
);

export const restoreStockEntry = createAsyncThunk(
  "stock/restore",
  async (id: number) => {
    await inventoryApi.restore(id);
    return id;
  },
);

const stockSlice = createSlice({
  name: "stock",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetSelected: (state) => {
      state.selectedEntry = null;
    },
    resetStockBalance: (state) => {
      state.stockBalance = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStockEntries.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStockEntries.fulfilled, (state, action) => {
        // state.loading = false;
        // state.entries = action.payload.transactions;
        // state.pagination = action.payload.pagination;
        state.loading = false;
        const { transactions, pagination } = action.payload;
        const { currentPage } = pagination;

        // ✅ Merge data: replace if page 1, otherwise append
        if (currentPage === 1) {
          state.entries = transactions;
        } else {
          // Avoid duplicates by filtering existing IDs if needed (optional)
          const existingIds = new Set(state.entries.map((e) => e.id));
          const newEntries = transactions.filter((e) => !existingIds.has(e.id));
          state.entries = [...state.entries, ...newEntries];
        }
        state.pagination = pagination;
      })
      .addCase(fetchStockEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch transactions";
      })
      .addCase(fetchStockEntryById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStockEntryById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedEntry = action.payload;
      })
      .addCase(fetchStockEntryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Transaction not found";
      })
      .addCase(fetchStockBalance.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStockBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.stockBalance = action.payload;
      })
      .addCase(fetchStockBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch stock balance";
      })
      .addCase(createStockEntry.pending, (state) => {
        state.updating = true;
      })
      .addCase(createStockEntry.fulfilled, (state, action) => {
        state.updating = false;
        state.entries.unshift(action.payload);
      })
      .addCase(createStockEntry.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Failed to add transaction";
      })
      .addCase(deleteStockEntry.pending, (state) => {
        state.updating = true;
      })
      .addCase(deleteStockEntry.fulfilled, (state, action) => {
        state.updating = false;
        state.entries = state.entries.filter((e) => e.id !== action.payload);
        if (state.selectedEntry?.id === action.payload)
          state.selectedEntry = null;
      })
      .addCase(deleteStockEntry.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Failed to delete transaction";
      })
      .addCase(restoreStockEntry.pending, (state) => {
        state.updating = true;
      })
      .addCase(restoreStockEntry.fulfilled, (state, action) => {
        state.updating = false;
        // Re-fetch entries to get the restored one (or just update isDeleted status if we had it)
        // For simplicity, we'll just refetch the list; the caller will refresh.
      })
      .addCase(restoreStockEntry.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Failed to restore transaction";
      });
  },
});

export const { clearError, resetSelected, resetStockBalance } =
  stockSlice.actions;
export default stockSlice.reducer;
